import Upload from "rc-upload";
import Trigger from "rc-trigger";
import { useState } from "react";
import { observer } from "mobx-react-lite";
import { Box, Center, useToast } from "@chakra-ui/react";

import { download, Graph, Lang, toJsonBlob } from "@noli/core";
import { BoardStore, langStore, translate, useSelecting } from "@noli/business";

import "./Menu.scss";
import { genBlock } from "@/utils/bem";
import { themeStore } from "@/stores/theme";
import { ReactComponent as MenuSvg } from "@/assets/icons/menu.svg";
import { ReactComponent as LinkOutSvg } from "@/assets/icons/link_out.svg";

import { onArrange, toast, useOwnToast } from "@/utils/misc";
import { Editor_Words } from "@/utils/lang/editor";
import { SafeOp } from "@/utils/safeOp";
import { loginStore } from "@/modules/login/store";
import Icon from "@/components/Icon";
import Uploader from "@/modules/editor/components/Uploader";
import IconButton from "@/components/IconButton";
import MenuOption from "./MenuOption";

const block = genBlock("c-header-menu");

const Separator = observer(() => {
  const { separatorColor } = themeStore.styles;
  return (
    <Center>
      <Box w="80%" h="0px" my="8px" borderTop="2px" borderColor={separatorColor}></Box>
    </Center>
  );
});

function onImport(t: ReturnType<typeof useToast>, lang: Lang, file: Blob): Promise<void> {
  toast(t, "success", translate(Editor_Words["importing-global-message"], lang));
  return new Promise((resolve) => {
    async function success(): Promise<void> {
      toast(t, "success", translate(Editor_Words["import-global-success-message"], lang));
      resolve();
    }
    async function failed(): Promise<void> {
      toast(t, "error", translate(Editor_Words["import-global-failed-message"], lang));
      resolve();
    }

    const reader = new FileReader();
    reader.onload = () =>
      SafeOp.importGraph(Graph.fromJson(reader.result as string), { success, failed });
    reader.readAsText(file);
  });
}

async function onDownload(t: ReturnType<typeof useToast>, lang: Lang): Promise<void> {
  toast(t, "success", translate(Editor_Words["downloading-message"], lang));
  const blob = toJsonBlob(BoardStore.graph.toJson());
  await download(blob, "exported.noli");
}

function HeaderMenu() {
  const [visible, setVisible] = useState(false);
  const t = useOwnToast();
  const lang = langStore.tgt;
  const { panelBg, iconColor } = themeStore.styles;
  const { type, nodes } = useSelecting("raw");

  const size = "32px";
  const hideMenu = () => setVisible(false);

  return (
    <Trigger
      action={["click"]}
      popupVisible={visible}
      onPopupVisibleChange={setVisible}
      popup={
        <Box
          w="180px"
          // h="176px"
          pt="20px"
          pb="20px"
          bg={panelBg}
          fontSize="14px"
          boxShadow="0px 0px 8px rgba(0, 0, 0, 0.25)"
        >
          <Upload
            className={block({ e: "upload" })}
            accept=".noli"
            customRequest={({ file }) => onImport(t, lang, file as Blob)}
          >
            <MenuOption hideMenu={hideMenu} text={translate(Editor_Words["import-global"], lang)} />
          </Upload>
          <MenuOption
            hideMenu={hideMenu}
            text={translate(Editor_Words["download-global"], lang)}
            onClick={() => onDownload(t, lang)}
          />
          {loginStore.isGuest ? null : (
            <>
              <Separator />
              <a href="/gallery" target="_blank">
                <MenuOption
                  hideMenu={hideMenu}
                  text={translate("menu_link-to-gallery", lang)}
                  action={
                    <Icon
                      squared={false}
                      className={block({ e: "option-icon" })}
                      svg={LinkOutSvg}
                    />
                  }
                />
              </a>
            </>
          )}
          <Separator />
          <MenuOption
            hideMenu={hideMenu}
            text={translate(Editor_Words["auto-arrange"], lang)}
            onClick={() =>
              onArrange(t, lang, { type: type === "multiple" ? "multiple" : "none", nodes })
            }
          />
          <Uploader>
            <MenuOption hideMenu={hideMenu} text={translate("upload-image", lang)} />
          </Uploader>
        </Box>
      }
      popupAlign={{
        points: ["tl", "bl"],
        offset: [12, -8],
      }}
    >
      <IconButton
        w={size}
        h={size}
        mr="10px"
        bg={panelBg}
        color={iconColor}
        className={block({ e: "icon" })}
        icon={MenuSvg}
        tip={translate(Editor_Words["header_menu_tip"], lang)}
      />
    </Trigger>
  );
}

export default observer(HeaderMenu);
